import React, { useState } from 'react';
import {
    Card, Select, Tag, Button, Space, Typography, Divider, Alert, Input,
} from 'antd';
import useIntents from './useIntents';

const { Text } = Typography;

const INTENT_HELP = {
    YES: 'Answers "Would you like to schedule?"-type questions.',
    NO: 'Declines. Also used for "not interested".',
    CONFIRM: 'Accepts a proposed time. "correct" and "yes" belong here too.',
    RESCHEDULE: 'Wants a different time.',
    CANCEL: 'Wants out of a booked interview.',
    MORE: 'Wants more time options.',
};

// gohire-admin editor for the intent dictionary — global phrases every module
// inherits, or one company's additions. Saving replaces the whole scope.
const IntentPanel = () => {
    const s = useIntents();
    const [tryValue, setTryValue] = useState('');
    const isGlobal = !s.companyId;

    return (
        <div className="gh-dash-grid">
            <Card
                className="gh-dash-card"
                title="Intent dictionary"
                extra={(
                    <Select
                        style={{ minWidth: 280 }}
                        value={s.companyId || 'global'}
                        onChange={(v) => s.setCompanyId(v === 'global' ? null : v)}
                        showSearch
                        optionFilterProp="label"
                        options={[
                            { label: 'Global — all companies', value: 'global' },
                            ...s.companies.map((c) => ({ label: `${c.name} (overrides)`, value: c.id })),
                        ]}
                    />
                )}
            >
                <Alert
                    type={isGlobal ? 'warning' : 'info'}
                    showIcon
                    style={{ marginBottom: 14 }}
                    message={isGlobal
                        ? 'Global phrases apply to every bot on every company immediately. A candidate reply that matches a phrase here is treated as the intent it names — for whichever answer the current question accepts.'
                        : 'Company phrases are added on top of the global dictionary for this company only (e.g. Spanish forms for a bilingual client).'}
                />

                {s.intents.map((intent) => (
                    <div key={intent.key} style={{ marginBottom: 14 }}>
                        <Space align="baseline" style={{ marginBottom: 4 }} wrap>
                            <Text strong>{intent.key}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>{INTENT_HELP[intent.key]}</Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                filter words: {intent.tokens.map((t) => <Tag key={t} style={{ fontSize: 10, marginRight: 4 }}>{t}</Tag>)}
                            </Text>
                        </Space>
                        <Select
                            mode="tags"
                            style={{ width: '100%' }}
                            tokenSeparators={[',']}
                            placeholder={isGlobal ? 'Phrases candidates actually type…' : 'Extra phrases for this company…'}
                            value={s.edited[intent.key] || []}
                            onChange={(list) => s.setPhrases(intent.key, list)}
                            open={false}
                        />
                    </div>
                ))}

                {s.errors.length > 0 && (
                    <Alert
                        type="error"
                        showIcon
                        style={{ marginTop: 4, marginBottom: 10 }}
                        message="Not saved"
                        description={(
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {s.errors.map((e) => <li key={e}>{e}</li>)}
                            </ul>
                        )}
                    />
                )}

                <Divider style={{ margin: '10px 0' }} />
                <Space wrap>
                    <Button type="primary" loading={s.saving} disabled={!s.dirty} onClick={s.save}>Save dictionary</Button>
                    <Button disabled={!s.dirty || s.saving} onClick={s.reset}>Reset</Button>
                    {isGlobal && <Button type="link" onClick={s.restoreDefaults}>Load defaults into editor</Button>}
                </Space>
            </Card>

            <Card className="gh-dash-card" title="Try it">
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                    Type what a candidate might reply. Shows how the bot normalises it and which intents it carries
                    {isGlobal ? '' : ' (with this company’s overrides)'}. Unsaved edits are not included.
                </Text>
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        placeholder='e.g. "Confirmar", "yes please", "1.", "what hotel"'
                        value={tryValue}
                        onChange={(e) => setTryValue(e.target.value)}
                        onPressEnter={() => s.tryText(tryValue)}
                    />
                    <Button type="primary" onClick={() => s.tryText(tryValue)}>Resolve</Button>
                </Space.Compact>
                {s.tryResult && (
                    <div style={{ marginTop: 12 }}>
                        <Space wrap>
                            <Text type="secondary">normalised:</Text>
                            <Tag>{s.tryResult.norm || '(empty)'}</Tag>
                            <Text type="secondary">intents:</Text>
                            {s.tryResult.intents.length
                                ? s.tryResult.intents.map((i) => <Tag color="purple" key={i}>{i}</Tag>)
                                : <Tag color="default">none — would reach the model fallback or the re-prompt</Tag>}
                        </Space>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default IntentPanel;
